/* eslint-disable max-lines */
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Tooltip,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  CircularProgress,
  ListItem,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  Autocomplete,
  LinearProgress,
  Divider,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Google as GoogleIcon,
  Message as MessageIcon,
  Web as WebIcon,
  Settings as SettingsIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Sync as SyncIcon,
  Schedule as ScheduleIcon,
  Webhook as WebhookIcon,
  Folder as FolderIcon,
  Storage as StorageIcon,
  Timer as TimerIcon,
  CloudQueue as CloudQueueIcon,
  AttachFile as AttachFileIcon,
  AudioFile as AudioFileIcon,
  CalendarToday as CalendarTodayIcon,
  Description as DescriptionIcon,
  Assessment as AssessmentIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useEntityContext } from '../EntityContextSwitch';
import api from '../../services/api';
import SlackSetupWizard from './Slack/SlackSetupWizard';

const INGESTION_TYPES = [
  {
    id: 'google_drive',
    label: 'Google Drive',
    icon: <GoogleIcon />,
    description: 'Monitor Google Drive folders for new files',
    available: true
  },
  {
    id: 'private_google_drive',
    label: 'Private Google Drive',
    icon: <LockIcon />,
    description: 'Private Drive folder — only visible to you',
    available: true,
    isPrivate: true
  },
  {
    id: 'slack',
    label: 'Slack Workspace',
    icon: <MessageIcon />,
    description: 'Collect messages and files from Slack channels',
    available: true
  },
  {
    id: 'google_forms',
    label: 'Google Forms',
    icon: <AssessmentIcon />,
    description: 'Sync responses from Google Forms',
    available: true
  },
  {
    id: 'api',
    label: 'API Webhook',
    icon: <WebIcon />,
    description: 'Receive data via webhook endpoint',
    available: false
  }
];

const CRON_SCHEDULES = [
  { value: '0 * * * *', label: 'Every hour' },
  { value: '0 */6 * * *', label: 'Every 6 hours' },
  { value: '0 */12 * * *', label: 'Every 12 hours' },
  { value: '0 0 * * *', label: 'Daily at midnight' },
  { value: '0 9 * * 1-5', label: 'Weekdays at 9 AM' },
  { value: 'custom', label: 'Custom schedule' }
];

// eslint-disable-next-line max-lines-per-function
function IngestionPoints() {
  const { user } = useAuth();
  const { currentEntity } = useEntityContext();
  const [ingestionPoints, setIngestionPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [syncProgress, setSyncProgress] = useState({});
  const [manifests, setManifests] = useState({});
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [newPointData, setNewPointData] = useState({
    type: 'google_drive',
    name: '',
    description: '',
    triggerType: 'scheduled',
    config: {
      folderId: '',
      includeSubfolders: false,
      filePatterns: [],
      excludePatterns: []
    },
    authentication: {
      method: 'service_account',
      impersonateEmail: user?.email || ''
    },
    scheduledConfig: {
      schedule: '0 */6 * * *',
      timezone: 'America/New_York'
    }
  });
  const [driveFolders, setDriveFolders] = useState([]);
  const [searchingFolders, setSearchingFolders] = useState(false);
  const [_folderSearchQuery, setFolderSearchQuery] = useState('');
  const [selectedDriveFolder, setSelectedDriveFolder] = useState(null);

  // Google Forms state
  const [googleForms, setGoogleForms] = useState([]);
  const [searchingForms, setSearchingForms] = useState(false);
  const [_formSearchQuery, setFormSearchQuery] = useState('');
  const [selectedGoogleForms, setSelectedGoogleForms] = useState([]);
  const [includeHistoricalResponses, setIncludeHistoricalResponses] = useState(false);
  const [selectedFormsForSync, setSelectedFormsForSync] = useState(new Set());

  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, point: null, confirmText: '' });
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [slackWizardOpen, setSlackWizardOpen] = useState(false);
  const [slackSetupData, setSlackSetupData] = useState(null);
  const [ingestExistingFiles, setIngestExistingFiles] = useState(true);
  const [confirmIngestDialogOpen, setConfirmIngestDialogOpen] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState(null);

  // Upload dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadMetadata, setUploadMetadata] = useState({
    recordingDate: new Date().toISOString().split('T')[0],
    callAssessment: '',
    callDescription: ''
  });

  useEffect(() => {
    if (currentEntity?.id) {
      fetchIngestionPoints();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEntity]);

  // Fetch manifest data for each ingestion point
  useEffect(() => {
    const fetchManifests = async () => {
      if (!currentEntity?.id || ingestionPoints.length === 0) return;

      const manifestPromises = ingestionPoints.map(async (point) => {
        try {
          const manifest = await api.getEntityIngestionManifest(currentEntity.id, point.id);
          return { [point.id]: manifest };
        } catch (err) {
          console.error(`Failed to fetch manifest for ${point.id}:`, err);
          return { [point.id]: null };
        }
      });

      const results = await Promise.all(manifestPromises);
      const manifestMap = results.reduce((acc, curr) => ({ ...acc, ...curr }), {});
      setManifests(manifestMap);
    };

    fetchManifests();
  }, [ingestionPoints, currentEntity]);

  const fetchIngestionPoints = async () => {
    if (!currentEntity?.id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await api.listEntityIngestionPoints(currentEntity.id);
      setIngestionPoints(response.ingestionPoints || []);
    } catch (err) {
      setError('Failed to fetch ingestion points: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const searchDriveFolders = async (query) => {
    if (!currentEntity?.id) {
      console.error('No client selected for Drive folder search');
      return;
    }

    setSearchingFolders(true);
    try {
      const response = await api.searchDriveFolders(query, currentEntity.id);
      setDriveFolders(response.folders || []);
    } catch (err) {
      console.error('Failed to search folders:', err);
    } finally {
      setSearchingFolders(false);
    }
  };

  const searchGoogleForms = async (query) => {
    if (!currentEntity?.id) {
      console.error('No entity selected for Google Forms search');
      return;
    }

    setSearchingForms(true);
    try {
      const response = await api.searchGoogleForms(query, currentEntity.id);
      setGoogleForms(response.forms || []);
    } catch (err) {
      console.error('Failed to search Google Forms:', err);
      setError('Failed to search Google Forms. Please try again.');
    } finally {
      setSearchingForms(false);
    }
  };

  const handleCreateIngestionPoint = (type = 'google_drive') => {
    if (!currentEntity?.id) {
      setError('Please select an entity before creating an ingestion point');
      return;
    }

    setSelectedPoint(null);

    // If creating a Slack ingestion point, open the wizard
    if (type === 'slack') {
      setSlackWizardOpen(true);
      return;
    }

    // Map private_google_drive UI type to google_drive backend type
    const isPrivate = type === 'private_google_drive';
    const backendType = isPrivate ? 'google_drive' : type;

    setIngestExistingFiles(true);
    setNewPointData({
      type: backendType,
      isPrivate,
      name: '',
      description: '',
      triggerType: 'scheduled',
      config: {
        folderId: '',
        includeSubfolders: false,
        filePatterns: [],
        excludePatterns: [],
        // Slack config
        workspaceId: '',
        channels: [],
        botConfig: {}
      },
      authentication: {
        method: 'service_account',
        impersonateEmail: user?.email || ''
      },
      scheduledConfig: {
        schedule: '0 */6 * * *',
        timezone: 'America/New_York'
      }
    });
    setDriveFolders([]);
    setFolderSearchQuery('');
    setConfigDialogOpen(true);
  };

  const handleEditIngestionPoint = (point) => {
    setSelectedPoint(point);
    setNewPointData({
      name: point.name,
      description: point.description || '',
      type: point.type,
      triggerType: point.trigger.type,
      config: point.config,
      authentication: point.authentication,
      scheduledConfig: point.trigger.scheduledConfig || {
        schedule: '0 */6 * * *',
        timezone: 'America/New_York'
      }
    });
    setConfigDialogOpen(true);
  };

  const handleSlackWizardComplete = async (setupData) => {
    try {
      setCreating(true);
      setError(null);

      const slackConfig = {
        type: 'slack',
        name: setupData.botConfig.name,
        description: setupData.botConfig.description,
        config: {
          workspaceId: setupData.workspace.id,
          channels: setupData.selectedChannels.map(channel => ({
            id: channel.id,
            name: channel.name,
            isPrivate: channel.isPrivate
          })),
          botConfig: setupData.botConfig
        },
        authentication: {
          method: 'oauth',
          workspaceToken: setupData.workspace.accessToken
        },
        triggerType: setupData.botConfig.realTimeSync ? 'webhook' : 'scheduled',
        triggerConfig: setupData.botConfig.realTimeSync ? undefined : {
          schedule: setupData.botConfig.syncSchedule || '*/15 * * * *',
          timezone: setupData.botConfig.timezone || 'America/New_York'
        }
      };

      await api.createEntityIngestionPoint(currentEntity.id, slackConfig);
      setSuccessMessage('Slack bot created successfully');
      setSlackWizardOpen(false);
      setSlackSetupData(null);
      fetchIngestionPoints();
    } catch (err) {
      setError('Failed to create Slack bot: ' + err.message);
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const handleSlackWizardCancel = () => {
    setSlackWizardOpen(false);
    setSlackSetupData(null);
  };

  // eslint-disable-next-line max-lines-per-function
  const handleSaveIngestionPoint = async () => {
    if (!currentEntity?.id) {
      setError('No client selected');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      // Handle Google Forms - create one ingestion point per selected form
      if (newPointData.type === 'google_forms' && !selectedPoint) {
        if (selectedGoogleForms.length === 0) {
          setError('Please select at least one Google Form');
          setCreating(false);
          return;
        }

        // Create one ingestion point for each selected form
        const createPromises = selectedGoogleForms.map(form => {
          const formConfig = {
            formId: form.id,
            formTitle: form.title,
            formUrl: form.formUrl || `https://docs.google.com/forms/d/${form.id}/edit`,
            syncSettings: {
              includeEditedResponses: true,
              includeHistoricalResponses: includeHistoricalResponses,
              startSyncFrom: includeHistoricalResponses ? null : new Date().toISOString()
            }
          };

          return api.createEntityIngestionPoint(currentEntity.id, {
            type: 'google_forms',
            name: form.title || 'Untitled Form',
            description: `Google Form: ${form.id}`,
            config: formConfig,
            authentication: newPointData.authentication,
            triggerType: 'scheduled',
            triggerConfig: {
              schedule: '*/5 * * * *', // Every 5 minutes
              timezone: 'America/New_York'
            }
          });
        });

        await Promise.all(createPromises);
        setSuccessMessage(`Created ${selectedGoogleForms.length} Google Forms ingestion point(s)`);

        // Clear form selections
        setSelectedGoogleForms([]);
        setIncludeHistoricalResponses(false);
      } else {
        // Handle other types (Drive, Slack, etc.) or updates
        const requestData = {
          type: newPointData.type,
          name: newPointData.name,
          description: newPointData.description,
          config: {
            ...newPointData.config,
            filePatterns: newPointData.config.filePatterns?.filter(p => p) || [],
            excludePatterns: newPointData.config.excludePatterns?.filter(p => p) || []
          },
          authentication: newPointData.authentication,
          triggerType: newPointData.triggerType,
          triggerConfig: newPointData.triggerType === 'scheduled' ? newPointData.scheduledConfig : undefined,
          ...(newPointData.isPrivate ? { visibility: 'private' } : {})
        };

        if (selectedPoint) {
          // Update existing
          await api.updateEntityIngestionPoint(currentEntity.id, {
            id: selectedPoint.id,
            ...requestData
          });
          setSuccessMessage('Ingestion point updated successfully');
        } else {
          // Create new — if ingestExistingFiles is checked and type is google_drive, show confirm first
          // eslint-disable-next-line max-depth
          if (newPointData.type === 'google_drive' && ingestExistingFiles && !pendingSaveData) {
            setPendingSaveData(requestData);
            setConfirmIngestDialogOpen(true);
            setCreating(false);
            return;
          }

          const result = await api.createEntityIngestionPoint(currentEntity.id, requestData);
          const newPointId = result.ingestionPointId;

          // After creation, handle existing files
          // eslint-disable-next-line max-depth
          if (newPointData.type === 'google_drive' && newPointId) {
            // eslint-disable-next-line max-depth
            if (ingestExistingFiles) {
              // Immediately sync all existing files
              // eslint-disable-next-line max-depth
              try {
                await api.syncEntityIngestionPoint(currentEntity.id, newPointId);
                setSuccessMessage('Ingestion point created and sync started');
              } catch (syncErr) {
                setSuccessMessage('Ingestion point created but sync failed to start: ' + syncErr.message);
              }
            } else {
              // Snapshot existing files so only new ones get processed
              // eslint-disable-next-line max-depth
              try {
                await api.snapshotEntityIngestionPoint(currentEntity.id, newPointId);
                setSuccessMessage('Ingestion point created — only new files will be processed');
              } catch (snapErr) {
                setSuccessMessage('Ingestion point created but snapshot failed: ' + snapErr.message);
              }
            }
          } else {
            setSuccessMessage('Ingestion point created successfully');
          }
        }
      }

      setConfigDialogOpen(false);
      setSelectedDriveFolder(null);
      setSelectedGoogleForms([]);
      setPendingSaveData(null);
      fetchIngestionPoints();
    } catch (err) {
      setError('Failed to save ingestion point: ' + err.message);
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const handleToggleStatus = async (point) => {
    if (!currentEntity?.id) {
      setError('No client selected');
      return;
    }

    try {
      await api.updateEntityIngestionPoint(currentEntity.id, {
        id: point.id,
        status: point.status === 'active' ? 'paused' : 'active'
      });

      setSuccessMessage(`Ingestion point ${point.status === 'active' ? 'paused' : 'activated'}`);
      fetchIngestionPoints();
    } catch (err) {
      setError('Failed to toggle status: ' + err.message);
      console.error(err);
    }
  };

  const handleDeleteIngestionPoint = (point) => {
    if (!currentEntity?.id) {
      setError('No client selected');
      return;
    }

    // Open the delete confirmation dialog
    setDeleteDialog({
      open: true,
      point,
      confirmText: ''
    });
  };

  const confirmDelete = async () => {
    const { point, confirmText } = deleteDialog;

    // Check if the user typed the correct confirmation
    if (confirmText !== point.name) {
      setError('Confirmation text does not match. Please type the exact name.');
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      await api.deleteEntityIngestionPoint(currentEntity.id, point.id);
      setSuccessMessage('Ingestion point deleted successfully');
      setDeleteDialog({ open: false, point: null, confirmText: '' });
      fetchIngestionPoints();
    } catch (err) {
      setError('Failed to delete ingestion point: ' + err.message);
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const handleManualSync = async (point) => {
    if (!currentEntity?.id) {
      setError('No client selected');
      return;
    }

    setSyncing({ ...syncing, [point.id]: true });
    setSyncProgress({ ...syncProgress, [point.id]: { phase: 'initializing', processed: 0, total: 0 } });

    try {
      const result = await api.syncEntityIngestionPoint(currentEntity.id, point.id);

      // Poll for sync progress
      const pollInterval = setInterval(async () => {
        try {
          const manifest = await api.getEntityIngestionManifest(currentEntity.id, point.id);
          if (manifest) {
            setManifests(prev => ({ ...prev, [point.id]: manifest }));

            // Update progress if sync is complete
            if (manifest.syncStatus !== 'syncing') {
              clearInterval(pollInterval);
              setSyncing(prev => ({ ...prev, [point.id]: false }));
              setSyncProgress(prev => {
                const updated = { ...prev };
                delete updated[point.id];
                return updated;
              });
            }
          }
        } catch (err) {
          console.error('Failed to poll sync status:', err);
        }
      }, 2000); // Poll every 2 seconds

      setSuccessMessage(`Sync started: ${result.message || 'Processing files...'}`);
      fetchIngestionPoints();
    } catch (err) {
      setError('Failed to sync: ' + err.message);
      console.error(err);
      setSyncing({ ...syncing, [point.id]: false });
      setSyncProgress(prev => {
        const updated = { ...prev };
        delete updated[point.id];
        return updated;
      });
    }
  };

  const handleSyncSelected = async () => {
    if (!currentEntity?.id) {
      setError('No entity selected');
      return;
    }

    if (selectedFormsForSync.size === 0) {
      setError('No forms selected for sync');
      return;
    }

    // Get all selected points
    const selectedPoints = ingestionPoints.filter(
      point => selectedFormsForSync.has(point.id) && point.type === 'google_forms'
    );

    // Filter out forms that are already syncing
    const formsToSync = selectedPoints.filter(point => {
      const manifest = manifests[point.id];
      const isCurrentlySyncing = syncing[point.id] || manifest?.syncStatus === 'syncing';
      return !isCurrentlySyncing;
    });

    if (formsToSync.length === 0) {
      setError('All selected forms are already syncing');
      return;
    }

    // Log skipped forms
    const skippedCount = selectedPoints.length - formsToSync.length;
    if (skippedCount > 0) {
      setSuccessMessage(`Skipping ${skippedCount} form(s) already syncing. Starting sync for ${formsToSync.length} form(s).`);
    }

    // Sync all selected forms in parallel
    try {
      await Promise.all(formsToSync.map(point => handleManualSync(point)));

      // Clear selection after starting syncs
      setSelectedFormsForSync(new Set());

      if (!skippedCount) {
        setSuccessMessage(`Started sync for ${formsToSync.length} form(s)`);
      }
    } catch (err) {
      setError(`Failed to start sync: ${err.message}`);
      console.error(err);
    }
  };

  const getStatusIcon = (status, manifest) => {
    // Check manifest sync status first
    if (manifest?.syncStatus === 'syncing') {
      return <SyncIcon color="info" className="rotating" />;
    }

    switch (status) {
      case 'active':
        return <CheckCircleIcon color="success" />;
      case 'paused':
        return <PauseIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'syncing':
        return <SyncIcon color="info" className="rotating" />;
      default:
        return null;
    }
  };

  const getSyncStatusChip = (manifest) => {
    if (!manifest) {
      return (
        <Chip
          label="Never Synced"
          size="small"
          color="default"
        />
      );
    }

    const statusMap = {
      'idle': { label: 'Ready', color: 'success' },
      'syncing': { label: 'Syncing', color: 'info' },
      'error': { label: 'Error', color: 'error' },
      'pending': { label: 'Never Synced', color: 'default' },
      'paused': { label: 'Paused', color: 'warning' }
    };

    const syncStatus = manifest.syncStatus || 'pending';
    const status = statusMap[syncStatus] || { label: syncStatus, color: 'default' };

    return (
      <Chip
        label={status.label}
        size="small"
        color={status.color}
        icon={syncStatus === 'syncing' ? <SyncIcon className="rotating" /> : null}
      />
    );
  };

  // Upload handlers
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Accept audio files and text-based files
      const acceptedTypes = [
        'audio/webm', 'audio/mp3', 'audio/wav', 'audio/m4a', 'audio/ogg', 'audio/mpeg',
        'text/plain', 'text/csv', 'application/json', 'text/markdown'
      ];

      if (!acceptedTypes.includes(file.type) && !file.name.endsWith('.md')) {
        setError('Please select an audio file (.webm, .mp3, .wav, .m4a, .ogg) or text file (.txt, .csv, .json, .md)');
        return;
      }

      // Check file size (max 50MB)
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        setError(`File too large. Maximum size is 50MB, your file is ${(file.size / 1024 / 1024).toFixed(2)}MB`);
        return;
      }

      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUploadClick = () => {
    setUploadDialogOpen(true);
    setSelectedFile(null);
    setUploadMetadata({
      recordingDate: new Date().toISOString().split('T')[0],
      callAssessment: '',
      callDescription: ''
    });
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile || !currentEntity?.id) {
      setError('Please select a file and ensure an entity is selected');
      return;
    }

    setUploadingFile(true);
    setError(null);

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.readAsDataURL(selectedFile);

      const base64Data = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
      });

      // Prepare metadata with the three new fields
      const metadata = {
        recordingDate: uploadMetadata.recordingDate,
        callAssessment: uploadMetadata.callAssessment,
        callDescription: uploadMetadata.callDescription,
        uploadedAt: new Date().toISOString(),
        uploadedBy: user?.email,
        fileType: selectedFile.type,
        originalFileName: selectedFile.name
      };

      // Upload file with metadata
      await api.uploadAudioToGCS({
        entityId: currentEntity.id,
        audioData: base64Data,
        mimeType: selectedFile.type || 'application/octet-stream',
        fileName: selectedFile.name,
        metadata: metadata
      });

      setSuccessMessage(`File uploaded successfully: ${selectedFile.name}`);
      setUploadDialogOpen(false);
      setSelectedFile(null);

      // Refresh ingestion points to show updated statistics
      fetchIngestionPoints();

    } catch (err) {
      console.error('Upload failed:', err);
      setError(`Failed to upload file: ${err.message}`);
    } finally {
      setUploadingFile(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">
          <CloudUploadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Ingestion Points
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AttachFileIcon />}
            onClick={handleUploadClick}
            disabled={!currentEntity}
          >
            Upload File
          </Button>
          {selectedFormsForSync.size > 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<SyncIcon />}
              onClick={handleSyncSelected}
              disabled={!currentEntity}
            >
              Sync Selected ({selectedFormsForSync.size})
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateIngestionPoint}
            disabled={!currentEntity}
          >
            Create Ingestion Point
          </Button>
        </Box>
      </Box>
      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      {!currentEntity && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body1" fontWeight="bold" gutterBottom>
            Entity Selection Required
          </Typography>
          <Typography variant="body2">
            Please select an entity from the dropdown in the header before managing ingestion points.
          </Typography>
        </Alert>
      )}
      {/* Loading state */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {/* Ingestion Points Grid */}
      {!loading && currentEntity && (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {ingestionPoints.length === 0 ? (
            <Grid size={12}>
              <Card sx={{ textAlign: 'center', py: 4 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    No ingestion points configured
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create an ingestion point to start collecting data from external sources
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            // eslint-disable-next-line max-lines-per-function
            (ingestionPoints.map(point => {
              const typeConfig = INGESTION_TYPES.find(t => t.id === point.type);
              const manifest = manifests[point.id];
              const isCurrentlySyncing = syncing[point.id] || manifest?.syncStatus === 'syncing';
              const progress = syncProgress[point.id];

              return (
                <Grid
                  key={point.id}
                  size={{
                    xs: 12,
                    sm: 6,
                    lg: 4
                  }}>
                  <Card sx={{
                    position: 'relative',
                    overflow: 'visible',
                    ...(isCurrentlySyncing && {
                      boxShadow: '0 0 20px rgba(25, 118, 210, 0.3)',
                      animation: 'pulse 2s infinite'
                    })
                  }}>
                    {point.type === 'google_forms' && (
                      <Checkbox
                        checked={selectedFormsForSync.has(point.id)}
                        onChange={(e) => {
                          const newSet = new Set(selectedFormsForSync);
                          if (e.target.checked) {
                            newSet.add(point.id);
                          } else {
                            newSet.delete(point.id);
                          }
                          setSelectedFormsForSync(newSet);
                        }}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1
                        }}
                      />
                    )}
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {typeConfig?.icon}
                          <Typography variant="h6" sx={{ ml: 1 }}>
                            {point.name}
                          </Typography>
                        </Box>
                        {getStatusIcon(point.status, manifest)}
                      </Box>

                      {point.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {point.description}
                        </Typography>
                      )}

                      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={typeConfig?.label || point.type}
                          size="small"
                        />
                        {point.visibility === 'private' && (
                          <Chip
                            label="Private"
                            size="small"
                            icon={<LockIcon />}
                            variant="outlined"
                            color="secondary"
                          />
                        )}
                        {getSyncStatusChip(manifest)}
                        <Chip
                          icon={point.trigger.type === 'webhook' ? <WebhookIcon /> : <ScheduleIcon />}
                          label={point.trigger.type}
                          size="small"
                          variant="outlined"
                        />
                      </Box>

                      {/* Sync Progress */}
                      {isCurrentlySyncing && progress && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            {progress.phase === 'initializing' && 'Initializing sync...'}
                            {progress.phase === 'scanning' && 'Scanning files...'}
                            {progress.phase === 'processing' && `Processing files (${progress.processed}/${progress.total})...`}
                            {progress.phase === 'finalizing' && 'Finalizing sync...'}
                          </Typography>
                          <LinearProgress
                            variant={progress.total > 0 ? "determinate" : "indeterminate"}
                            value={progress.total > 0 ? (progress.processed / progress.total) * 100 : 0}
                            sx={{ mt: 1 }}
                          />
                        </Box>
                      )}

                      {/* Statistics - combining ingestion point and manifest data */}
                      {(manifest || point.statistics.lastSync) && (
                        <>
                          <Divider sx={{ my: 2 }} />

                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                <StorageIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                Total Files
                              </Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {(manifest?.statistics?.totalFiles || point.statistics?.totalFiles || 0).toLocaleString()}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                <SyncIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                Total Syncs
                              </Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {(point.statistics?.syncCount || 0).toLocaleString()}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                <CloudQueueIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                Total Size
                              </Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {formatBytes(manifest?.statistics?.totalSize || point.statistics?.totalSize || 0)}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                <TimerIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                Last Duration
                              </Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {manifest?.statistics?.lastSyncDuration || point.statistics?.lastSyncDuration
                                  ? `${manifest?.statistics?.lastSyncDuration || point.statistics.lastSyncDuration}s`
                                  : 'N/A'}
                              </Typography>
                            </Box>
                          </Box>

                          {(manifest?.lastSyncTimestamp || point.statistics?.lastSync) && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                              Last sync: {manifest?.lastSyncTimestamp
                                ? new Date(manifest.lastSyncTimestamp._seconds * 1000).toLocaleString()
                                : new Date(point.statistics.lastSync._seconds * 1000).toLocaleString()}
                            </Typography>
                          )}

                          {/* Last Sync Details */}
                          {(manifest?.statistics?.lastSyncFileCount !== undefined || point.statistics?.lastSyncFileCount !== undefined) && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                Last Sync Results:
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Typography variant="caption">
                                  <CheckCircleIcon sx={{ fontSize: 12, verticalAlign: 'middle', color: 'success.main' }} />
                                  {' '}{manifest?.statistics?.lastSyncFilesAdded || point.statistics?.lastSyncFilesAdded || 0} added
                                </Typography>
                                <Typography variant="caption">
                                  <SyncIcon sx={{ fontSize: 12, verticalAlign: 'middle', color: 'info.main' }} />
                                  {' '}{manifest?.statistics?.lastSyncFilesUpdated || point.statistics?.lastSyncFilesUpdated || 0} updated
                                </Typography>
                                <Typography variant="caption">
                                  <DeleteIcon sx={{ fontSize: 12, verticalAlign: 'middle', color: 'error.main' }} />
                                  {' '}{manifest?.statistics?.lastSyncFilesDeleted || point.statistics?.lastSyncFilesDeleted || 0} deleted
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </>
                      )}

                      {/* Error Display */}
                      {manifest?.statistics.lastError && manifest?.syncStatus === 'error' && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          {manifest.statistics.lastError}
                        </Alert>
                      )}
                    </CardContent>

                    <CardActions>
                      <Tooltip title={point.status === 'active' ? 'Pause' : 'Start'}>
                        <IconButton
                          onClick={() => handleToggleStatus(point)}
                          color={point.status === 'active' ? 'warning' : 'success'}
                          disabled={isCurrentlySyncing}
                        >
                          {point.status === 'active' ? <PauseIcon /> : <PlayArrowIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isCurrentlySyncing ? "Sync in progress" : "Manual sync"}>
                        <IconButton
                          onClick={() => handleManualSync(point)}
                          disabled={point.status !== 'active' || isCurrentlySyncing}
                        >
                          {isCurrentlySyncing ? <CircularProgress size={24} /> : <SyncIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Configure">
                        <IconButton
                          onClick={() => handleEditIngestionPoint(point)}
                          disabled={isCurrentlySyncing}
                        >
                          <SettingsIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isCurrentlySyncing ? "Cannot delete while syncing" : "Delete"}>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteIngestionPoint(point)}
                          disabled={isCurrentlySyncing}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Grid>
              );
            }))
          )}
        </Grid>
      )}
      {/* Configuration Dialog */}
      <Dialog
        open={configDialogOpen}
        onClose={() => {
          setConfigDialogOpen(false);
          setSelectedDriveFolder(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedPoint ? 'Edit Ingestion Point' : 'Create Ingestion Point'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Basic Information */}
            <Grid size={12}>
              <TextField
                fullWidth
                label="Name"
                value={newPointData.name}
                onChange={(e) => setNewPointData({ ...newPointData, name: e.target.value })}
                required
              />
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                label="Description"
                value={newPointData.description}
                onChange={(e) => setNewPointData({ ...newPointData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>

            {!selectedPoint && (
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={newPointData.isPrivate ? 'private_google_drive' : newPointData.type}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === 'private_google_drive') {
                        setNewPointData({ ...newPointData, type: 'google_drive', isPrivate: true });
                      } else {
                        setNewPointData({ ...newPointData, type: val, isPrivate: false });
                      }
                    }}
                    label="Type"
                  >
                    {INGESTION_TYPES.map(type => (
                      <MenuItem key={type.id} value={type.id} disabled={!type.available}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {type.icon}
                          <Box sx={{ ml: 1 }}>
                            <Typography>{type.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {type.description}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Google Drive Configuration */}
            {newPointData.type === 'google_drive' && (
              <>
                <Grid size={12}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                    Google Drive Configuration
                  </Typography>
                </Grid>

                <Grid size={12}>
                  <Autocomplete
                    options={driveFolders}
                    getOptionLabel={(option) => option.path || option.name}
                    loading={searchingFolders}
                    value={selectedDriveFolder}
                    onChange={(e, value) => {
                      setSelectedDriveFolder(value);
                      setNewPointData({
                        ...newPointData,
                        config: { ...newPointData.config, folderId: value?.id || '' }
                      });
                    }}
                    onInputChange={(e, value) => {
                      setFolderSearchQuery(value);
                      if (value) searchDriveFolders(value);
                    }}
                    sx={{
                      '& .MuiAutocomplete-listbox': {
                        backgroundColor: 'background.paper',
                        '& .MuiAutocomplete-option': {
                          color: 'text.primary'
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Google Drive Folder"
                        required
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <FolderIcon />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <>
                              {searchingFolders ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <ListItem {...props} sx={{
                        backgroundColor: 'background.paper',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        },
                        '& .MuiListItemText-primary': {
                          color: 'text.primary'
                        },
                        '& .MuiListItemText-secondary': {
                          color: 'text.secondary'
                        }
                      }}>
                        <ListItemIcon>
                          <FolderIcon sx={{ color: 'text.secondary' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={option.name}
                          secondary={option.path}
                        />
                      </ListItem>
                    )}
                  />
                </Grid>

                <Grid size={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newPointData.config.includeSubfolders || false}
                        onChange={(e) => setNewPointData({
                          ...newPointData,
                          config: { ...newPointData.config, includeSubfolders: e.target.checked }
                        })}
                      />
                    }
                    label="Include subfolders"
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <TextField
                    fullWidth
                    label="File patterns to include"
                    placeholder="*.pdf, *.docx"
                    value={newPointData.config.filePatterns?.join(', ') || ''}
                    onChange={(e) => setNewPointData({
                      ...newPointData,
                      config: {
                        ...newPointData.config,
                        filePatterns: e.target.value.split(',').map(p => p.trim()).filter(p => p)
                      }
                    })}
                    helperText="Comma-separated patterns (e.g., *.pdf)"
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <TextField
                    fullWidth
                    label="File patterns to exclude"
                    placeholder="~$*, *.tmp"
                    value={newPointData.config.excludePatterns?.join(', ') || ''}
                    onChange={(e) => setNewPointData({
                      ...newPointData,
                      config: {
                        ...newPointData.config,
                        excludePatterns: e.target.value.split(',').map(p => p.trim()).filter(p => p)
                      }
                    })}
                    helperText="Comma-separated patterns"
                  />
                </Grid>

                {/* Ingest existing files checkbox - only on create */}
                {!selectedPoint && (
                  <Grid size={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={ingestExistingFiles}
                          onChange={(e) => setIngestExistingFiles(e.target.checked)}
                        />
                      }
                      label="Ingest all existing files in this folder"
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                      {ingestExistingFiles
                        ? 'All existing files will be immediately ingested and processed'
                        : 'Only new files added after setup will be processed'}
                    </Typography>
                  </Grid>
                )}
              </>
            )}

            {/* Google Forms Configuration */}
            {newPointData.type === 'google_forms' && (
              <>
                <Grid size={12}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                    Google Forms Configuration
                  </Typography>
                </Grid>

                <Grid size={12}>
                  <Autocomplete
                    multiple
                    options={googleForms}
                    getOptionLabel={(option) => option.title || 'Untitled Form'}
                    loading={searchingForms}
                    value={selectedGoogleForms}
                    onChange={(e, value) => {
                      setSelectedGoogleForms(value);
                    }}
                    onInputChange={(e, value) => {
                      setFormSearchQuery(value);
                      if (value) searchGoogleForms(value);
                    }}
                    sx={{
                      '& .MuiAutocomplete-listbox': {
                        backgroundColor: 'background.paper',
                        '& .MuiAutocomplete-option': {
                          color: 'text.primary'
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Google Forms"
                        placeholder="Search for forms..."
                        required
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <AssessmentIcon />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                          endAdornment: (
                            <>
                              {searchingForms ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        helperText="Search and select one or more forms to sync"
                      />
                    )}
                    renderOption={(props, option) => (
                      <ListItem {...props} sx={{
                        backgroundColor: 'background.paper',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        },
                        '& .MuiListItemText-primary': {
                          color: 'text.primary'
                        },
                        '& .MuiListItemText-secondary': {
                          color: 'text.secondary'
                        }
                      }}>
                        <ListItemIcon>
                          <AssessmentIcon sx={{ color: 'text.secondary' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={option.title}
                          secondary={option.id}
                        />
                      </ListItem>
                    )}
                  />
                </Grid>

                <Grid size={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeHistoricalResponses}
                        onChange={(e) => setIncludeHistoricalResponses(e.target.checked)}
                      />
                    }
                    label="Include historical responses (all responses from form creation)"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                    If disabled, only new responses from this point forward will be synced
                  </Typography>
                </Grid>
              </>
            )}

            {/* Slack Configuration */}
            {newPointData.type === 'slack' && (
              <>
                <Grid size={12}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                    Slack Configuration
                  </Typography>
                </Grid>

                <Grid size={12}>
                  <Alert severity="info">
                    For Slack ingestion points, please use the guided setup wizard which will walk you through connecting your workspace, selecting channels, and configuring the bot.
                  </Alert>
                </Grid>

                <Grid size={12}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<MessageIcon />}
                    onClick={() => {
                      setConfigDialogOpen(false);
                      setSlackWizardOpen(true);
                    }}
                  >
                    Open Slack Setup Wizard
                  </Button>
                </Grid>
              </>
            )}

            {/* Trigger Configuration */}
            <Grid size={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Sync Trigger
              </Typography>
            </Grid>

            <Grid size={12}>
              <FormControl>
                <RadioGroup
                  value={newPointData.triggerType}
                  onChange={(e) => setNewPointData({ ...newPointData, triggerType: e.target.value })}
                >
                  <FormControlLabel
                    value="scheduled"
                    control={<Radio />}
                    label="Scheduled (run on a regular schedule)"
                  />
                  <FormControlLabel
                    value="webhook"
                    control={<Radio />}
                    label="Real-time (webhook - coming soon)"
                    disabled
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            {newPointData.triggerType === 'scheduled' && (
              <>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <FormControl fullWidth>
                    <InputLabel>Schedule</InputLabel>
                    <Select
                      value={newPointData.scheduledConfig.schedule === 'custom' ? 'custom' :
                             CRON_SCHEDULES.find(s => s.value === newPointData.scheduledConfig.schedule)?.value || 'custom'}
                      onChange={(e) => setNewPointData({
                        ...newPointData,
                        scheduledConfig: {
                          ...newPointData.scheduledConfig,
                          schedule: e.target.value === 'custom' ? newPointData.scheduledConfig.schedule : e.target.value
                        }
                      })}
                      label="Schedule"
                    >
                      {CRON_SCHEDULES.map(schedule => (
                        <MenuItem key={schedule.value} value={schedule.value}>
                          {schedule.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <TextField
                    fullWidth
                    label="Timezone"
                    value={newPointData.scheduledConfig.timezone}
                    onChange={(e) => setNewPointData({
                      ...newPointData,
                      scheduledConfig: { ...newPointData.scheduledConfig, timezone: e.target.value }
                    })}
                  />
                </Grid>

                {(newPointData.scheduledConfig.schedule === 'custom' ||
                  !CRON_SCHEDULES.find(s => s.value === newPointData.scheduledConfig.schedule)) && (
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Custom cron expression"
                      value={newPointData.scheduledConfig.schedule}
                      onChange={(e) => setNewPointData({
                        ...newPointData,
                        scheduledConfig: { ...newPointData.scheduledConfig, schedule: e.target.value }
                      })}
                      helperText="e.g., 0 */4 * * * (every 4 hours)"
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfigDialogOpen(false);
              setSelectedDriveFolder(null);
            }}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveIngestionPoint}
            disabled={
              creating ||
              (newPointData.type === 'google_drive' && (!newPointData.name || !newPointData.config.folderId)) ||
              (newPointData.type === 'google_forms' && selectedGoogleForms.length === 0) ||
              (newPointData.type !== 'google_drive' && newPointData.type !== 'google_forms' && !newPointData.name)
            }
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {creating ? 'Creating...' : (selectedPoint ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, point: null, confirmText: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteIcon color="error" />
            <Typography variant="h6">Delete Ingestion Point</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The ingestion point will be permanently deleted and all scheduled syncs will be stopped.
          </Alert>
          {deleteDialog.point && (
            <>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete the ingestion point <strong>"{deleteDialog.point.name}"</strong>?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 3 }}>
                Type <strong>{deleteDialog.point.name}</strong> below to confirm:
              </Typography>
              <TextField
                fullWidth
                label="Confirmation"
                variant="outlined"
                value={deleteDialog.confirmText}
                onChange={(e) => setDeleteDialog({ ...deleteDialog, confirmText: e.target.value })}
                placeholder={`Type "${deleteDialog.point.name}" to confirm`}
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && deleteDialog.confirmText === deleteDialog.point.name) {
                    confirmDelete();
                  }
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, point: null, confirmText: '' })}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deleting || !deleteDialog.point || deleteDialog.confirmText !== deleteDialog.point.name}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Confirm Ingest Existing Files Dialog */}
      <Dialog
        open={confirmIngestDialogOpen}
        onClose={() => {
          setConfirmIngestDialogOpen(false);
          setPendingSaveData(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm File Ingestion</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will immediately download and process ALL existing files in the selected Drive folder. Depending on the folder size, this may take several minutes.
          </Alert>
          <Typography variant="body2">
            Are you sure you want to ingest all existing files?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmIngestDialogOpen(false);
              setPendingSaveData(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              setConfirmIngestDialogOpen(false);
              if (pendingSaveData) {
                setCreating(true);
                try {
                  const result = await api.createEntityIngestionPoint(currentEntity.id, pendingSaveData);
                  const newPointId = result.ingestionPointId;
                  if (newPointId) {
                    // eslint-disable-next-line max-depth
                    try {
                      await api.syncEntityIngestionPoint(currentEntity.id, newPointId);
                      setSuccessMessage('Ingestion point created and sync started');
                    } catch (syncErr) {
                      setSuccessMessage('Ingestion point created but sync failed: ' + syncErr.message);
                    }
                  }
                  setConfigDialogOpen(false);
                  setSelectedDriveFolder(null);
                  fetchIngestionPoints();
                } catch (err) {
                  setError('Failed to save ingestion point: ' + err.message);
                } finally {
                  setCreating(false);
                  setPendingSaveData(null);
                }
              }
            }}
          >
            Yes, Ingest All Files
          </Button>
        </DialogActions>
      </Dialog>
      {/* Upload File Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => !uploadingFile && setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudUploadIcon color="primary" />
            Upload File with Metadata
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* File Selection */}
            <Box sx={{ mb: 3 }}>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept="audio/*,.txt,.csv,.json,.md"
                onChange={handleFileSelect}
              />
              <Button
                variant="outlined"
                fullWidth
                startIcon={selectedFile ? <AudioFileIcon /> : <AttachFileIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  py: 2,
                  border: '2px dashed',
                  borderColor: selectedFile ? 'primary.main' : 'divider',
                  bgcolor: selectedFile ? 'action.hover' : 'transparent'
                }}
              >
                {selectedFile ? selectedFile.name : 'Click to select file'}
              </Button>
              {selectedFile && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Size: {formatBytes(selectedFile.size)} | Type: {selectedFile.type || 'Unknown'}
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Metadata Fields */}
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              File Metadata
            </Typography>

            {/* Recording Date */}
            <TextField
              fullWidth
              label="Recording Date"
              type="date"
              value={uploadMetadata.recordingDate}
              onChange={(e) => setUploadMetadata(prev => ({ ...prev, recordingDate: e.target.value }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarTodayIcon />
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ mb: 2 }}
              helperText="Date when this recording or document was created"
            />

            {/* Call Assessment */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="call-assessment-label">Call Assessment</InputLabel>
              <Select
                labelId="call-assessment-label"
                value={uploadMetadata.callAssessment}
                onChange={(e) => setUploadMetadata(prev => ({ ...prev, callAssessment: e.target.value }))}
                label="Call Assessment"
                startAdornment={
                  <InputAdornment position="start">
                    <AssessmentIcon />
                  </InputAdornment>
                }
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="excellent">Excellent</MenuItem>
                <MenuItem value="good">Good</MenuItem>
                <MenuItem value="average">Average</MenuItem>
                <MenuItem value="needs_improvement">Needs Improvement</MenuItem>
                <MenuItem value="follow_up_required">Follow-up Required</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Overall assessment of the call or meeting
              </Typography>
            </FormControl>

            {/* Call Description */}
            <TextField
              fullWidth
              label="Call Description"
              multiline
              rows={4}
              value={uploadMetadata.callDescription}
              onChange={(e) => setUploadMetadata(prev => ({ ...prev, callDescription: e.target.value }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                    <DescriptionIcon />
                  </InputAdornment>
                ),
              }}
              helperText="Provide a brief description of the call content, key points discussed, or document context"
              sx={{ mb: 2 }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUploadDialogOpen(false)}
            disabled={uploadingFile}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUploadSubmit}
            variant="contained"
            disabled={!selectedFile || uploadingFile}
            startIcon={uploadingFile ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
          >
            {uploadingFile ? 'Uploading...' : 'Upload File'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Slack Setup Wizard Dialog */}
      <Dialog
        open={slackWizardOpen}
        onClose={handleSlackWizardCancel}
        maxWidth="lg"
        fullWidth
        fullScreen
      >
        <SlackSetupWizard
          entityId={currentEntity?.id}
          onComplete={handleSlackWizardComplete}
          onCancel={handleSlackWizardCancel}
          initialData={slackSetupData}
        />
      </Dialog>
      <style jsx>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .rotating {
          animation: rotate 1s linear infinite;
        }
        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(25, 118, 210, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
          }
        }
      `}</style>
    </Box>
  );
}

export default IngestionPoints;